import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppSelector } from "hooks/hook";
import _, { chain, findKey, keys, map, max, min, pickBy } from "lodash";
import { BookmarkCheck, Table as DTable, LayoutPanelTop, ListTodo, Users } from "lucide-react";
import moment from "moment-timezone";
import React, { useCallback, useEffect } from "react";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import { shallowEqual, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Card, CardBody, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from "reactstrap";
import { changeSetting } from "store/reducers/settingsSlice";
import { useFilteredDockets } from "../../hooks/useFilteredDockets";
import useUrlParams from "../../hooks/useUrlParams";
import MenuItemsPopUp from "../../oms/MenuItemPopup";
import IntervalRangeFilter from "../../resbutler-ui/components/Elements/IntervalRangeFilter";
import MultiToggle, { MultiToggleOption } from "../../resbutler-ui/components/Elements/MultiToggle";
import { getDateFromNow, getMealLabelIds } from "../../resbutler-utils/utils";
import { IntervalRange, intervalRangeFilter } from "../../resbutler-utils/utils/filters";
import { loadFilterPrinterIds } from "../../helpers/printerFilterStorage";
import { OrderDisplayMode, setDisplay, setFilterPrinterIds, setIntervalRange, setOrderDisplayMode, setOrderMode, setProgress, setShowSummary } from "../../store/reducers/rootSlice";
import { roles } from "resbutler-utils";
import ToggleOptions from "../Common/ToggleOptions";
import { ON_CLICK_DEBOUNCE_WAIT } from "./../../constants";
import { OperationMode } from "./Offsidebar";
import { canAccessModule } from "resbutler-utils/utils/accessUtils";

export enum DisplayType {
  PrepDisplay = 0,
  ServeDisplay = 1,
}

export enum ProgressType {
  Active = 0,
  Completed = 1,
  All = 2,
}

export enum OrderModeType {
  MenuPlan = 0,
  SentOrder = 1,
  BasketOrder = 2,
}

const OmsSelector = React.memo<{ hiddenKey: string }>(function OmsSelector({ hiddenKey }) {
  const [restaurantId, date] = useUrlParams();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { restaurants, orderModeType, operationMode, printers, display, progress, labels, showSummary, meals, intervalRange, orderDisplayType, bookings, user } = useAppSelector(
    (state) => ({
      restaurants: state.root.restaurants,
      orderModeType: state.root.orderModeType,
      operationMode: state.root.operationMode,
      printers: state.root.printers,
      display: state.root.display,
      progress: state.root.progress,
      showSummary: state.root.showSummary,
      labels: state.root.labels,
      meals: state.root.meals,
      intervalRange: state.root.intervalRange,
      orderDisplayType: state.root.orderDisplayMode,
      bookings: state.root.bookings,
      user: state.root.user,
    }),
    shallowEqual
  );
  const claims = user?.customClaims;
  const isSystem = roles.hasRole(claims?.roles ?? 0, roles.roles.SYSTEM) !== 0;
  const canMenuControl = isSystem || canAccessModule(claims?.grants, "pd", "menuControl");
  const { completedDocketNumber, activeDockets, allCount } = useFilteredDockets(hiddenKey);

  const mealLabels = getMealLabelIds(labels, restaurants[restaurantId].zoneId, date, restaurantId);
  const restaurantMeals = pickBy(meals, (meal, mId) => meal.restaurantId === restaurantId && meal.enabled && findKey(mealLabels, (ml) => ml.mealId === mId) !== undefined);

  useEffect(() => {
    const savedPrinterIds = loadFilterPrinterIds(printers);
    dispatch(setFilterPrinterIds(savedPrinterIds ?? keys(printers)));
  }, [printers]);

  const getTodayBtnStyle = (date: string, currentDate: string) => {
    const style: any = { borderColor: "#27c24c" };
    if (date === currentDate) {
      style.backgroundColor = "#27c24c";
      style.color = "white";
    } else {
      style.backgroundColor = "#fff";
      style.color = "#27c24c";
    }
    return style;
  };

  const renderTodayDateTime = () => {
    const now = moment.unix(Date.now() / 1000).tz(restaurants[restaurantId].zoneId);
    const currentDate = getDateFromNow(now);
    return (
      <Button style={getTodayBtnStyle(date, currentDate)} className="w-100" onClick={() => navigate(`${pathname}#${currentDate}/${restaurantId}`)}>
        Today
      </Button>
    );
  };

  const renderOrderModeToggle = () => {
    const options: MultiToggleOption[] = [
      {
        label: "Menu Plans",
        value: OrderModeType.MenuPlan,
        icon: ListTodo,
      },
      {
        label: "Sent Orders",
        value: OrderModeType.SentOrder,
        icon: BookmarkCheck,
      },
    ];

    return <MultiToggle options={options} selectedOption={orderModeType} onSelectOption={(value) => dispatch(setOrderMode(value))} />;
  };

  const renderProgress = () => (
    <UncontrolledDropdown className="btn-group w-50">
      <DropdownToggle caret>
        <span className="font-weight-bold ">
          {progress === ProgressType.All && <>All ({allCount})</>}
          {progress === ProgressType.Active && <>Active ({activeDockets})</>}
          {progress === ProgressType.Completed && <>Completed ({completedDocketNumber})</>}
        </span>
      </DropdownToggle>
      <DropdownMenu>
        <DropdownItem
          value={ProgressType.All}
          onClick={() => {
            dispatch(setProgress(ProgressType.All));
          }}
        >
          All ({allCount})
        </DropdownItem>
        <DropdownItem
          value={ProgressType.Active}
          onClick={() => {
            dispatch(setProgress(ProgressType.Active));
          }}
        >
          Active ({activeDockets})
        </DropdownItem>
        <DropdownItem
          value={ProgressType.Completed}
          onClick={() => {
            dispatch(setProgress(ProgressType.Completed));
            changeSetting({ name: "showHiddenDockets", value: false });
          }}
        >
          Completed ({completedDocketNumber})
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );

  const renderDisplay = () => (
    <UncontrolledDropdown className="btn-group w-50">
      <DropdownToggle caret>
        <span className="text">{display === DisplayType.PrepDisplay ? <b>Prep Display</b> : <b>Serve Display</b>}</span>
      </DropdownToggle>
      <DropdownMenu>
        <DropdownItem
          value={DisplayType.PrepDisplay}
          onClick={() => {
            dispatch(setDisplay(DisplayType.PrepDisplay));
          }}
        >
          Prep Display
        </DropdownItem>
        <DropdownItem
          value={DisplayType.ServeDisplay}
          onClick={() => {
            dispatch(setDisplay(DisplayType.ServeDisplay));
          }}
        >
          Serve Display
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );

  const renderDateTime = () => {
    return (
      <div className="btn-group">
        <Button className="rounded-start" onClick={_.debounce(() => navigate(`${pathname}#${moment(date, "YYYYMMDD").add(-1, "days").format("YYYYMMDD")}/${restaurantId}`), ON_CLICK_DEBOUNCE_WAIT)}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </Button>
        <Datetime className="text-center" inputProps={{ className: "text-center form-control rounded-0", style: { minWidth: 150 } }} dateFormat="ddd DD MMMM YYYY" closeOnSelect onChange={(e) => navigate(`${pathname}#${moment(e).format("YYYYMMDD")}/${restaurantId}`)} timeFormat={false} value={moment(date, "YYYYMMDD")} />
        <Button className="rounded-end" onClick={_.debounce(() => navigate(`${pathname}#${moment(date, "YYYYMMDD").add(1, "days").format("YYYYMMDD")}/${restaurantId}`), ON_CLICK_DEBOUNCE_WAIT)}>
          <FontAwesomeIcon icon={faChevronRight} />
        </Button>
      </div>
    );
  };

  const toggle = useCallback(() => {
    dispatch(setShowSummary(!showSummary));
  }, [showSummary]);

  const renderToggleSummary = () => {
    return <ToggleOptions onClick={toggle} on={"Summary"} off={"Summary"} active={showSummary} width={"100%"} className="w-50" />;
  };

  const handleTimeChange = (range: IntervalRange) => {
    dispatch(setIntervalRange(range));
  };

  const filteredBookings = _.chain(bookings)
    .pickBy((booking) => intervalRangeFilter(booking, intervalRange))
    .value();

  const totalPax = filteredBookings ? _.values(filteredBookings).reduce((sum, b) => sum + b.pax, 0) : 0;

  return (
    <>
      <Card className="oms-selector card-default mt-2" style={{ backgroundColor: "#555E65" }}>
        <CardBody style={{ padding: "0px" }}>
          {/* top row */}
          <div className="grid-container">
            <div className="grid-item">
              <div className="d-flex gap-2">
                {renderDateTime()}
                <div>{renderTodayDateTime()}</div>
              </div>
            </div>
            <div className="grid-item">
              {canMenuControl && <MenuItemsPopUp restaurantId={restaurantId} date={date} />}
            </div>
            <div className="grid-item">{renderOrderModeToggle()}</div>
            <div className="grid-item">
              <div className="btn-group d-flex gap-2 w-100">
                {orderModeType === OrderModeType.SentOrder && operationMode === OperationMode.AllStep ? renderDisplay() : null}
                {orderModeType === OrderModeType.SentOrder ? renderProgress() : null}
              </div>
            </div>
            <div className="grid-item">
              <div className="btn-group w-100 justify-content-end">{renderToggleSummary()}</div>
            </div>
          </div>

          {/* 2nd row */}
          {orderModeType !== OrderModeType.SentOrder && (
            <div className="d-grid grid-container">
              <div className="grid-item text-nowrap d-flex align-content-center">
                <MultiToggle
                  options={[
                    { value: OrderDisplayMode.Docket, label: "Docket", icon: LayoutPanelTop },
                    { value: OrderDisplayMode.Table, label: "List", icon: DTable },
                  ]}
                  color="primary"
                  size="md"
                  onSelectOption={(value) => dispatch(setOrderDisplayMode(value))}
                  selectedOption={orderDisplayType}
                  classNames="me-4"
                />
                <h4 className="pt-2">
                  <Users size={20} className="" />: <span className=" font-weight-bold ps-2 pe-1">{totalPax}</span>
                  <span className=" font-weight-light">pax</span>
                </h4>
              </div>
              <div className="grid-item-2">
                {!_.isEmpty(restaurantMeals) ? (
                  <IntervalRangeFilter //
                    minInterval={min(map(restaurantMeals, "activityStartTime"))}
                    maxInterval={max(map(restaurantMeals, (item) => (item.activityEndTime <= item.activityStartTime ? item.activityEndTime + 24 * 60 * 60 * 1000 : item.activityEndTime)))}
                    step={30}
                    onChange={handleTimeChange}
                    markers={chain(restaurantMeals)
                      .map((item) => ({
                        label: item.name,
                        start: item.activityStartTime,
                        end: item.activityEndTime <= item.activityStartTime ? item.activityEndTime + 24 * 60 * 60 * 1000 : item.activityEndTime,
                      }))
                      .sortBy("order")
                      .value()}
                    selectedRange={intervalRange}
                  />
                ) : null}
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
});

export default OmsSelector;
